import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import { t, Trans } from "@lingui/macro";
import { Heading } from "@otrium/atoms";
import { omit } from "lodash-es";
import { locale } from "src/config/app-config";
import {
  useConsentUpdated,
  useCartMinLineItemsTotalPriceDiff,
  useAppActions,
} from "src/hooks/stores/use-app-store";
import { PageHead } from "src/molecules/PageHead";
import { useCartData } from "src/hooks/stores/use-cart-store";
import { useValidateLineItemSupplyChannel } from "src/hooks/useValidateLineItemSupplyChannel";
import { getDiscountCodeDoesntMatch } from "src/utils/ct/discountCodeHelper";
import { hasRejectedTalonOneDiscount } from "src/utils/appliedDiscounts";
import { CheckoutStepInfo } from "./CheckoutStepInfo";
import CheckoutStepPayment from "./CheckoutStepPayment";
import { CheckoutWarning } from "src/molecules/CheckoutWarning";
import { Stepper, Step } from "src/molecules/Stepper";
import classNames from "classnames";
import styles from "./CheckoutView.module.scss";
import { clearFeedbackAnswers } from "./utils";
import { localeAdapter, useSegment } from "src/hooks/useSegment";
import { setLongCookie, VisitorType } from "src/utils/prospect-vs-customer";
import { useMiscellaneous } from "src/hooks/useMiscellaneous";
import { useLingui } from "@lingui/react";
import { useMyOrders } from "src/hooks/useMyOrders";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import currencyMap from "src/utils/currencyMap";
import { useSession } from "next-auth/react";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { sha256Email } from "src/segment";
import Cookies from "js-cookie";
import { CategoryPreferences } from "@ht-sdks/consent-manager/types/types";
import { COOKIE_NAME } from "src/molecules/ConsentMangerBanner";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { cartStore } from "src/stores/cart/cart.store";
import {
  getStorageJSON,
  removeStorageItem,
} from "src/utils/helper/localStorage";
import { OT_CHECKOUT_TRACKING_DATA } from "src/utils/constants";
import { isConfirmationStep } from "src/utils/checkout";
import { forgetGuestCartRef } from "src/utils/helper/guestSession";
import { CheckoutTrackingData } from "src/types/checkoutTracking";

const isIOSApp =
  typeof webkit !== "undefined" &&
  typeof webkit.messageHandlers?.openPaymentScreen?.postMessage !== "undefined";

const isAndroidApp = typeof webview !== "undefined";

const steps = [1, 2, 3];

function CheckoutView(): JSX.Element {
  const consentUpdated = useConsentUpdated();
  const cartMinLineItemsTotalPriceDiff = useCartMinLineItemsTotalPriceDiff();
  const { setPageViewedProperties } = useAppActions();

  const { currentShopType } = useShopTypeContext();
  const { data: session } = useSession();
  const { isLoggedIn } = useNewIsLoggedIn();
  const router = useRouter();
  const { segmentOrderCompleted, segmentPageViewed } = useSegment();

  const { cart, loading: cartLoading } = useCartData();
  const cartData = useMemo(() => ({ cart }), [cart]);

  const { refetch: refetchMyOrders } = useMyOrders({ skip: true });
  const { i18n } = useLingui();

  const routerStep: Step | undefined = useMemo(
    () =>
      steps.includes(Number(router.query.step as string))
        ? (Number(router.query.step as string) as Step)
        : undefined,
    [router.query.step]
  );

  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>("");

  const paymentCompletedRef = useRef(false);
  const trackedCartIdRef = useRef("");

  const { data: miscellaneousData } = useMiscellaneous();

  const isCartValid = !!cartData.cart?.lineItems?.length;
  const discountCodeDoesNotMatch =
    getDiscountCodeDoesntMatch(cartData.cart) ||
    hasRejectedTalonOneDiscount(cartData.cart);

  useEffect(() => {
    setPageViewedProperties({ page_type: "checkout" });
  }, [setPageViewedProperties]);

  useEffect(() => {
    if (router.query.payment_id || paymentCompletedRef.current) {
      // If payment is pending, we don't want to redirect to /cart in any way
      return;
    }

    // The query is empty until the router is ready, so redirecting before then
    // sends a step 3 shopper, whose cart was just cleared, to /cart.
    if (!router.isReady) {
      return;
    }

    const isCartLoading = cartLoading || !cartData.cart;
    if (
      (!isCartLoading && !isCartValid && !isConfirmationStep(router)) ||
      discountCodeDoesNotMatch ||
      cartMinLineItemsTotalPriceDiff?.centAmount > 0
    ) {
      void router.push("/cart");
    }
  }, [
    cartData.cart,
    isCartValid,
    cartMinLineItemsTotalPriceDiff?.centAmount,
    router,
    discountCodeDoesNotMatch,
    cartLoading,
  ]);

  useEffect(() => {
    if (!routerStep && router.isReady) {
      void router.replace({
        pathname: "/checkout",
        query: { ...router.query, step: 1 },
      });
    }
  }, [routerStep, router]);

  const { validating: validatingLineItems } = useValidateLineItemSupplyChannel(
    cartData.cart,
    setShowWarning,
    setWarningMessage
  );

  const checkoutCart =
    isCartValid &&
    !discountCodeDoesNotMatch &&
    !validatingLineItems &&
    !cartMinLineItemsTotalPriceDiff?.centAmount
      ? cartData.cart
      : null;

  // NOTE: Temporary solution for Native Apps to
  // show Native Payment Screen in WebView: https://otrium.atlassian.net/browse/TT-2476
  const openNativePaymentScreen = useCallback((): void => {
    if (isIOSApp) {
      webkit.messageHandlers.openPaymentScreen.postMessage("open");
    } else if (isAndroidApp) {
      webview.openPaymentScreen("open");
    }
  }, []);

  const onNextStep = useCallback(() => {
    void openNativePaymentScreen();

    void router.push({
      pathname: "/checkout",
      query: { ...router.query, step: 2 },
    });
  }, [openNativePaymentScreen, router]);

  const onPaymentStepCompleted = useCallback(
    async (args: { isPaymentPending: boolean }) => {
      if (paymentCompletedRef.current) {
        return;
      }

      paymentCompletedRef.current = true;
      const { data: myOrdersData } = await refetchMyOrders();

      clearFeedbackAnswers();

      const storedTrackingData = getStorageJSON<CheckoutTrackingData>(
        OT_CHECKOUT_TRACKING_DATA
      );

      if (storedTrackingData) {
        const hashedEmail = session?.user?.email
          ? sha256Email(session?.user?.email)
          : undefined;
        const isOldCustomer =
          myOrdersData?.me?.orders?.results?.length > 0 || false;

        const cookiePreferences = Cookies.getJSON(COOKIE_NAME) as
          | { custom?: CategoryPreferences }
          | undefined;

        const consentStatus = cookiePreferences?.custom
          ? Object.keys(cookiePreferences?.custom)
              .filter((key) => cookiePreferences.custom?.[key])
              .join("||")
          : undefined;

        trackedCartIdRef.current = storedTrackingData.cartId;
        setLongCookie(VisitorType.CUSTOMER);

        const shouldNotTriggerPageViewed = cookiePreferences
          ? !consentUpdated
          : false;

        if (!shouldNotTriggerPageViewed) {
          await segmentPageViewed({
            locale,
            pathName: router.asPath,
            cartData: {
              cart_total: formatStandardPrice(storedTrackingData.totalPrice),
              product_count: storedTrackingData.lineItemsCount,
            },
            gtmProperties: {
              page_type: "checkout",
              page_location: window.location.href,
              shop_locale: localeAdapter(locale),
              platform: "web",
              shop_currency: currencyMap[locale],
              shop_segment: currentShopType || "women",
              website_name: "otrium",
              logged_in: isLoggedIn,
              user_id: session?.user?.user_id,
              eid_sha256: hashedEmail,
              consent_status: consentStatus || "functional",
              customer_type: isOldCustomer ? "old customer" : "new customer",
            },
          });
        }

        await segmentOrderCompleted({
          trackingData: storedTrackingData,
          isOldCustomer,
        });

        if (typeof window !== "undefined") {
          window.cnstrc = window.cnstrc || {};
          window.cnstrc.purchaseData = {
            revenue:
              (storedTrackingData.taxedPrice?.totalGross.centAmount ?? 0) / 100,
            orderId: storedTrackingData.cartId,
            // TODO: post-cnstrctr-integration — variation_id intentionally
            // omitted: Constructor catalog (event-engine
            // mapProductToConstructor) does not emit a variations field, so
            // the CT variant id would not reconcile. Re-add once the catalog
            // mapper publishes variations with a matching id format.
            items: storedTrackingData.trackingProducts
              .filter((p) => p.product_id)
              .map((p) => ({
                item_id: p.product_id as string,
                count: p.quantity,
                price: p.price,
              })),
          };
        }

        removeStorageItem(OT_CHECKOUT_TRACKING_DATA);
      }

      // The paid cart must never become a merge candidate on a later login;
      // the replacement cart re-registers itself once it has line items.
      forgetGuestCartRef();
      cartStore.setCart(null);
      cartStore.setShouldCreateNewCart(true);

      const adyenQueryParams = [
        "sessionId",
        "redirectResult",
        "type",
        "resultCode",
      ];
      const mollieQueryParams = ["payment_id"];

      void router.push({
        pathname: "/checkout",
        query: {
          ...omit(router.query, adyenQueryParams, mollieQueryParams),
          ...(args.isPaymentPending ? { paymentStatus: "pending" } : {}),
          step: 3,
        },
      });
    },
    [
      locale,
      router,
      session?.user?.user_id,
      session?.user?.email,
      currentShopType,
      isLoggedIn,
      consentUpdated,
      refetchMyOrders,
      segmentPageViewed,
      segmentOrderCompleted,
    ]
  );

  return (
    <>
      <PageHead title={t(i18n)`Checkout | Otrium`} />
      <div className={styles.content}>
        <div
          className={classNames(
            styles.header,
            showWarning && styles.headerWithWarning
          )}
        >
          <Heading as="h1" variant="h1">
            <Trans>Checkout</Trans>
          </Heading>

          <Stepper activeStep={routerStep ?? 1} />

          <CheckoutWarning
            showWarning={showWarning}
            errorMessage={warningMessage}
            onClose={() => setShowWarning(false)}
          />
        </div>

        {routerStep === 1 && (
          <CheckoutStepInfo
            cart={checkoutCart}
            miscellaneous={miscellaneousData?.miscellaneous}
            setShowWarning={setShowWarning}
            setWarningMessage={setWarningMessage}
            onNextStep={onNextStep}
          />
        )}

        {routerStep === 2 && (
          <CheckoutStepPayment
            cart={checkoutCart}
            cartLoading={cartLoading}
            miscellaneous={miscellaneousData?.miscellaneous}
            setShowWarning={setShowWarning}
            setWarningMessage={setWarningMessage}
            onNextStep={(args: { isPaymentPending: boolean }) => {
              void onPaymentStepCompleted(args);
            }}
          />
        )}
      </div>
    </>
  );
}

export default CheckoutView;
